import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Download, History, ArrowDownCircle, ArrowUpCircle, FileSpreadsheet, Printer } from 'lucide-react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

export default function Reports() {
  const [stock, setStock] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [loading, setLoading] = useState(true);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemHistory, setItemHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    fetchWarehouses();
  }, []);

  useEffect(() => {
    fetchStock();
  }, [selectedWarehouse]);

  const fetchWarehouses = async () => {
    try {
      const res = await api.get('/warehouses');
      setWarehouses(res.data || []);
    } catch (error) {
      console.error('Failed to fetch warehouses:', error);
      toast.error('Failed to fetch warehouses');
      setWarehouses([]);
    }
  };

  const fetchStock = async () => {
    try {
      const url = selectedWarehouse 
        ? `/stock?warehouse=${selectedWarehouse}`
        : '/stock';
      const res = await api.get(url);
      setStock(res.data || []);
    } catch (error) {
      console.error('Failed to fetch stock:', error);
      toast.error('Failed to fetch stock');
      setStock([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async (item) => {
    setHistoryLoading(true);
    try {
      const url = selectedWarehouse 
        ? `/stock/history/${item.item_id}?warehouse=${selectedWarehouse}`
        : `/stock/history/${item.item_id}`;
      const res = await api.get(url);
      setItemHistory(res.data || []);
    } catch (error) {
      console.error('Failed to fetch history:', error);
      toast.error('Failed to fetch item history');
      setItemHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleItemClick = (item) => {
    setSelectedItem(item);
    setHistoryDialogOpen(true);
    fetchHistory(item);
  };

  const groupedStock = Array.isArray(stock) ? stock.reduce((acc, item) => {
    if (item && item.warehouse) {
      if (!acc[item.warehouse]) {
        acc[item.warehouse] = [];
      }
      acc[item.warehouse].push(item);
    }
    return acc;
  }, {}) : {};

  const handleExportExcel = async () => {
    try {
      const url = selectedWarehouse 
        ? `/export/stock?warehouse=${selectedWarehouse}`
        : '/export/stock';
      
      const response = await api.get(url, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `Stock_Report_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success('Stock report exported successfully!');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export stock report');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Calculate running balance for history
  const historyWithBalance = [...itemHistory].reverse().reduce((acc, item, idx) => {
    const prevBalance = idx > 0 ? acc[idx - 1].balance : 0;
    const change = item.type === 'IN' ? item.quantity : -item.quantity;
    const balance = prevBalance + change;
    acc.push({ ...item, balance });
    return acc;
  }, []).reverse();

  const handleExportHistoryExcel = () => {
    if (!selectedItem || historyWithBalance.length === 0) {
      toast.error('Tidak ada data untuk diexport');
      return;
    }
    const totalIn = itemHistory.filter(h => h.type === 'IN').reduce((s, h) => s + h.quantity, 0);
    const totalOut = itemHistory.filter(h => h.type === 'OUT').reduce((s, h) => s + h.quantity, 0);
    const rows = [
      [`Movement History - ${selectedItem.item_name}`],
      [`Warehouse: ${selectedItem.warehouse}`, '', `Unit: ${selectedItem.unit}`, '', `Current Stock: ${selectedItem.quantity}`],
      [],
      ['Date', 'Type', 'Reference', 'Document', 'Supplier/Made By', 'Qty', 'Balance'],
      ...historyWithBalance.map(h => [
        formatDate(h.date),
        h.type,
        h.reference || '',
        h.document || '',
        h.supplier || h.made_by || '',
        h.type === 'IN' ? h.quantity : -h.quantity,
        h.balance,
      ]),
      [],
      ['', '', '', '', 'Total IN', totalIn],
      ['', '', '', '', 'Total OUT', totalOut],
      ['', '', '', '', 'Transactions', itemHistory.length],
      ['', '', '', '', 'Current Stock', selectedItem.quantity],
    ];
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = [{ wch: 14 }, { wch: 6 }, { wch: 22 }, { wch: 18 }, { wch: 24 }, { wch: 10 }, { wch: 10 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Movement History');
    const buf = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
    const safeName = selectedItem.item_name.replace(/[^a-z0-9]/gi, '_').slice(0, 40);
    saveAs(new Blob([buf], { type: 'application/octet-stream' }), `MovementHistory_${safeName}_${selectedItem.warehouse}.xlsx`);
    toast.success('Excel berhasil diexport');
  };

  const handlePrint = () => {
    if (!selectedItem || historyWithBalance.length === 0) {
      toast.error('Tidak ada data untuk dicetak');
      return;
    }
    const totalIn = itemHistory.filter(h => h.type === 'IN').reduce((s, h) => s + h.quantity, 0);
    const totalOut = itemHistory.filter(h => h.type === 'OUT').reduce((s, h) => s + h.quantity, 0);
    const rowsHtml = historyWithBalance.map(h => `
      <tr>
        <td>${formatDate(h.date)}</td>
        <td style="text-align:center">${h.type}</td>
        <td>${h.reference || ''}${h.supplier ? `<br/><small>${h.supplier}</small>` : ''}${h.made_by ? `<br/><small>by ${h.made_by}</small>` : ''}</td>
        <td>${h.document || '-'}</td>
        <td style="text-align:right;color:${h.type === 'IN' ? '#15803d' : '#b91c1c'}">${h.type === 'IN' ? '+' : '-'}${h.quantity}</td>
        <td style="text-align:right;font-weight:600">${h.balance}</td>
      </tr>`).join('');
    const html = `<!doctype html><html><head><title>Movement History - ${selectedItem.item_name}</title>
      <style>
        body{font-family:Arial,sans-serif;padding:24px;color:#111}
        h1{font-size:18px;margin:0 0 4px}
        .meta{display:flex;gap:24px;margin-bottom:16px;font-size:12px;color:#555}
        table{width:100%;border-collapse:collapse;font-size:12px}
        th,td{border:1px solid #ccc;padding:6px 8px;text-align:left}
        thead{background:#f3f4f6}
        .summary{margin-top:16px;display:flex;gap:16px;font-size:12px}
        .summary div{border:1px solid #ccc;padding:8px 12px;border-radius:6px}
        @media print{button{display:none}}
      </style></head><body>
      <h1>Movement History - ${selectedItem.item_name}</h1>
      <div class="meta">
        <span><b>Warehouse:</b> ${selectedItem.warehouse}</span>
        <span><b>Unit:</b> ${selectedItem.unit}</span>
        <span><b>Current Stock:</b> ${selectedItem.quantity}</span>
        <span><b>Printed:</b> ${new Date().toLocaleString('id-ID')}</span>
      </div>
      <table>
        <thead><tr><th>Date</th><th>Type</th><th>Reference</th><th>Document</th><th style="text-align:right">Qty</th><th style="text-align:right">Balance</th></tr></thead>
        <tbody>${rowsHtml}</tbody>
      </table>
      <div class="summary">
        <div><b>Total IN:</b> ${totalIn}</div>
        <div><b>Total OUT:</b> ${totalOut}</div>
        <div><b>Transactions:</b> ${itemHistory.length}</div>
        <div><b>Current Stock:</b> ${selectedItem.quantity}</div>
      </div>
      <script>window.onload=()=>{window.print();}</script>
      </body></html>`;
    const w = window.open('', '_blank');
    w.document.write(html);
    w.document.close();
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div className="space-y-6 fade-in" data-testid="reports-page">
      <div>
        <h1 className="text-4xl font-bold text-primary mb-2">Stock Reports</h1>
        <p className="text-muted-foreground">View inventory stock by warehouse - Click item to see movement history</p>
      </div>

      <Card className="p-6">
        <div className="mb-6 flex items-end justify-between">
          <div className="flex-1 max-w-xs">
            <Label>Filter by Warehouse</Label>
            <Select value={selectedWarehouse || "all"} onValueChange={(value) => setSelectedWarehouse(value === "all" ? "" : value)}>
              <SelectTrigger className="bg-white border-2 border-primary/30">
                <SelectValue placeholder="All Warehouses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Warehouses</SelectItem>
                {warehouses.map((wh) => (
                  <SelectItem key={wh.id} value={wh.id}>
                    {wh.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button 
            onClick={handleExportExcel}
            className="btn-primary"
            data-testid="export-excel-button"
          >
            <Download className="h-4 w-4 mr-2" />
            Export to Excel
          </Button>
        </div>

        {stock.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground text-lg">No stock data available</p>
            <p className="text-sm text-muted-foreground mt-2">
              Stock will appear here after you release Barang Masuk documents
            </p>
          </div>
        ) : selectedWarehouse ? (
          <div>
            <h3 className="text-xl font-semibold text-primary mb-4">{selectedWarehouse}</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-secondary/50">
                    <th className="text-left p-3 font-semibold">Item Name</th>
                    <th className="text-center p-3 font-semibold">Unit</th>
                    <th className="text-right p-3 font-semibold">Quantity</th>
                    <th className="text-center p-3 font-semibold">History</th>
                  </tr>
                </thead>
                <tbody>
                  {stock.map((item, idx) => (
                    <tr 
                      key={idx} 
                      className="border-b hover:bg-accent/20 transition-colors cursor-pointer"
                      onClick={() => handleItemClick(item)}
                    >
                      <td className="p-3">{item.item_name}</td>
                      <td className="p-3 text-center">
                        <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-primary/10 text-primary">
                          {item.unit}
                        </span>
                      </td>
                      <td className="p-3 text-right tabular-nums font-medium text-lg">{item.quantity}</td>
                      <td className="p-3 text-center">
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleItemClick(item);
                          }}
                          title="View History"
                        >
                          <History className="h-4 w-4 text-primary" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(groupedStock).map(([warehouse, items]) => (
              <div key={warehouse}>
                <h3 className="text-xl font-semibold text-primary mb-4">{warehouse}</h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-secondary/50">
                        <th className="text-left p-3 font-semibold">Item Name</th>
                        <th className="text-center p-3 font-semibold">Unit</th>
                        <th className="text-right p-3 font-semibold">Quantity</th>
                        <th className="text-center p-3 font-semibold">History</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, idx) => (
                        <tr 
                          key={idx} 
                          className="border-b hover:bg-accent/20 transition-colors cursor-pointer"
                          onClick={() => handleItemClick(item)}
                        >
                          <td className="p-3">{item.item_name}</td>
                          <td className="p-3 text-center">
                            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-primary/10 text-primary">
                              {item.unit}
                            </span>
                          </td>
                          <td className="p-3 text-right tabular-nums font-medium text-lg">{item.quantity}</td>
                          <td className="p-3 text-center">
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleItemClick(item);
                              }}
                              title="View History"
                            >
                              <History className="h-4 w-4 text-primary" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-2">
              <span className="flex items-center gap-2">
                <History className="h-5 w-5" />
                Movement History
              </span>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleExportHistoryExcel}
                  disabled={!itemHistory.length}
                  data-testid="export-history-excel-btn"
                >
                  <FileSpreadsheet className="h-4 w-4 mr-1" /> Export Excel
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handlePrint}
                  disabled={!itemHistory.length}
                  data-testid="print-history-btn"
                >
                  <Printer className="h-4 w-4 mr-1" /> Print
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          {selectedItem && (
            <div className="space-y-4">
              {/* Item Info */}
              <div className="bg-secondary/30 p-4 rounded-lg">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Item</p>
                    <p className="font-semibold">{selectedItem.item_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Unit</p>
                    <p className="font-semibold">{selectedItem.unit}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Warehouse</p>
                    <p className="font-semibold">{selectedItem.warehouse}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Current Stock</p>
                    <p className="font-semibold text-xl text-primary">{selectedItem.quantity}</p>
                  </div>
                </div>
              </div>

              {/* History Table */}
              {historyLoading ? (
                <div className="text-center py-8">Loading history...</div>
              ) : itemHistory.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No movement history found for this item
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-secondary/50">
                        <th className="text-left p-3 font-semibold">Date</th>
                        <th className="text-center p-3 font-semibold">Type</th>
                        <th className="text-left p-3 font-semibold">Reference</th>
                        <th className="text-left p-3 font-semibold">Document</th>
                        <th className="text-right p-3 font-semibold">Qty</th>
                        <th className="text-right p-3 font-semibold">Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {historyWithBalance.map((item, idx) => (
                        <tr key={idx} className="border-b hover:bg-accent/10">
                          <td className="p-3 text-sm">{formatDate(item.date)}</td>
                          <td className="p-3 text-center">
                            {item.type === 'IN' ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                <ArrowDownCircle className="h-3 w-3" />
                                IN
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                <ArrowUpCircle className="h-3 w-3" />
                                OUT
                              </span>
                            )}
                          </td>
                          <td className="p-3">
                            <span className="font-mono text-sm">{item.reference}</span>
                            {item.supplier && (
                              <p className="text-xs text-muted-foreground">{item.supplier}</p>
                            )}
                            {item.made_by && (
                              <p className="text-xs text-muted-foreground">by {item.made_by}</p>
                            )}
                          </td>
                          <td className="p-3 text-sm">{item.document || '-'}</td>
                          <td className="p-3 text-right tabular-nums">
                            <span className={item.type === 'IN' ? 'text-green-600' : 'text-red-600'}>
                              {item.type === 'IN' ? '+' : '-'}{item.quantity}
                            </span>
                          </td>
                          <td className="p-3 text-right tabular-nums font-medium">{item.balance}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Summary */}
              {itemHistory.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t">
                  <div className="bg-green-50 p-3 rounded-lg text-center">
                    <p className="text-sm text-green-600">Total IN</p>
                    <p className="text-xl font-bold text-green-700">
                      {itemHistory.filter(h => h.type === 'IN').reduce((sum, h) => sum + h.quantity, 0)}
                    </p>
                  </div>
                  <div className="bg-red-50 p-3 rounded-lg text-center">
                    <p className="text-sm text-red-600">Total OUT</p>
                    <p className="text-xl font-bold text-red-700">
                      {itemHistory.filter(h => h.type === 'OUT').reduce((sum, h) => sum + h.quantity, 0)}
                    </p>
                  </div>
                  <div className="bg-blue-50 p-3 rounded-lg text-center">
                    <p className="text-sm text-blue-600">Transactions</p>
                    <p className="text-xl font-bold text-blue-700">{itemHistory.length}</p>
                  </div>
                  <div className="bg-primary/10 p-3 rounded-lg text-center">
                    <p className="text-sm text-primary">Current Stock</p>
                    <p className="text-xl font-bold text-primary">{selectedItem.quantity}</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
